import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import DashboardLayout from "../components/organisms/Layout/Layout";
import SearchBar from "../components/atoms/SearchBar";

// Import APIs
import Company from "../api/Company";
import Brand from "../api/Brand";
import Region from "../api/Region";
import Location from "../api/Location";
import JobSeeker from "../api/JobSeeker";
import User from "../api/User";

// Simple debounce implementation
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const GlobalSearch = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQuery = searchParams.get("q") || "";

  // State management
  const [searchQuery, setSearchQuery] = useState(urlQuery);
  const [activeTab, setActiveTab] = useState("companies");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);

  // Initialize search query from URL on mount
  useEffect(() => {
    if (urlQuery) {
      setSearchQuery(urlQuery);
      debouncedSearch(urlQuery, activeTab, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update search query when URL param changes (external navigation)
  useEffect(() => {
    if (urlQuery && urlQuery !== searchQuery) {
      setSearchQuery(urlQuery);
      setCurrentPage(1);
      debouncedSearch(urlQuery, activeTab, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlQuery]);

  // Perform search when activeTab or currentPage changes
  useEffect(() => {
    if (searchQuery.trim()) {
      debouncedSearch(searchQuery, activeTab, currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentPage]);

  // Tab configuration
  const tabs = [
    { id: "companies", label: "Companies", icon: "far fa-building" },
    { id: "brands", label: "Brands", icon: "far fa-tag" },
    { id: "regions", label: "Regions", icon: "far fa-map" },
    { id: "locations", label: "Locations", icon: "far fa-map-marker-alt" },
    { id: "job-seekers", label: "Job Seekers", icon: "far fa-users" },
    { id: "business-users", label: "Business Users", icon: "far fa-user-tie" },
  ];

  // API call functions
  const searchAPIs = {
    companies: (query, page = 1) =>
      Company.getAll(
        `search=${encodeURIComponent(query)}&page=${page}&limit=10`
      ),
    brands: (query, page = 1) =>
      Brand.getAll(`search=${encodeURIComponent(query)}&page=${page}&limit=10`),
    regions: (query, page = 1) =>
      Region.getAll(
        `search=${encodeURIComponent(query)}&page=${page}&limit=10`
      ),
    locations: (query, page = 1) =>
      Location.getAll(
        `search=${encodeURIComponent(query)}&page=${page}&limit=10`
      ),
    "job-seekers": (query, page = 1) =>
      JobSeeker.getAll(
        `search=${encodeURIComponent(query)}&page=${page}&limit=10`
      ),
    "business-users": (query, page = 1) =>
      User.getAll(`search=${encodeURIComponent(query)}&page=${page}&limit=10`),
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (query, tab, page = 1) => {
      if (!query.trim()) {
        setSearchResults([]);
        setTotalResults(0);
        setTotalPages(1);
        return;
      }

      setLoading(true);
      try {
        const response = await searchAPIs[tab](query, page);
        const data = response.data;

        // Handle different response structures
        if (data.data) {
          setSearchResults(data.data);
          setTotalResults(data.total || data.data.length);
          setTotalPages(Math.ceil((data.total || data.data.length) / 10));
        } else if (Array.isArray(data)) {
          setSearchResults(data);
          setTotalResults(data.length);
          setTotalPages(1);
        } else {
          setSearchResults([]);
          setTotalResults(0);
          setTotalPages(1);
        }
      } catch (error) {
        console.error("Search error:", error);
        setSearchResults([]);
        setTotalResults(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  // Handle search input change
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
    // Update URL parameter
    if (value.trim()) {
      setSearchParams({ q: value });
    } else {
      setSearchParams({});
    }
    debouncedSearch(value, activeTab, 1);
  };

  // Handle tab change
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setCurrentPage(1);
    setSearchResults([]);
    setTotalResults(0);
    setTotalPages(1);

    if (searchQuery.trim()) {
      debouncedSearch(searchQuery, tabId, 1);
    }
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (searchQuery.trim()) {
      debouncedSearch(searchQuery, activeTab, page);
    }
  };

  // Render search result item based on type
  const renderResultItem = (item, type) => {
    switch (type) {
      case "companies":
        return (
          <div className="p-4 border border-gray-200 rounded-lg bg-white hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {item.name || item.company_name}
                </h3>
                <p className="text-sm text-gray-600">
                  Owner:{" "}
                  {item.owners && item.owners.length > 0
                    ? `${item.owners[0].first_name} ${item.owners[0].last_name}`
                    : item.owner_name || item.owner || "N/A"}
                </p>
                <p className="text-sm text-gray-600">
                  Locations:{" "}
                  {item.locations?.length ||
                    item.locations_count ||
                    item.location_count ||
                    0}
                </p>
                {item.is_enterprise === 1 && (
                  <p className="text-sm text-gray-600">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      Enterprise
                    </span>
                  </p>
                )}
              </div>
              <div className="text-right">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Company
                </span>
              </div>
            </div>
          </div>
        );

      case "brands":
        return (
          <div className="p-4 border border-gray-200 rounded-lg bg-white hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {item.name || item.brand_name}
                </h3>
                <p className="text-sm text-gray-600">
                  Company: {item.company_name || item.company?.name || "N/A"}
                </p>
                {item.description && (
                  <p className="text-sm text-gray-600 mt-1">
                    {item.description}
                  </p>
                )}
              </div>
              <div className="text-right">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  Brand
                </span>
              </div>
            </div>
          </div>
        );

      case "regions":
        return (
          <div className="p-4 border border-gray-200 rounded-lg bg-white hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {item.name || item.region_name}
                </h3>
                <p className="text-sm text-gray-600">
                  Company: {item.company_name || item.company?.name || "N/A"}
                </p>
                {item.description && (
                  <p className="text-sm text-gray-600 mt-1">
                    {item.description}
                  </p>
                )}
              </div>
              <div className="text-right">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Region
                </span>
              </div>
            </div>
          </div>
        );

      case "locations":
        return (
          <div className="p-4 border border-gray-200 rounded-lg bg-white hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {item.name || item.location_name}
                </h3>
                <p className="text-sm text-gray-600">
                  {item.address?.address || item.full_address || "No address"}
                </p>
                <p className="text-sm text-gray-600">
                  Company: {item.company_name || item.company?.name || "N/A"}
                </p>
                {item.category && (
                  <p className="text-sm text-gray-600">
                    Category:{" "}
                    {Array.isArray(item.category) && item.category.length > 0
                      ? item.category[0].name
                      : item.category?.name || item.category}
                  </p>
                )}
                {item.region && (
                  <p className="text-sm text-gray-600">
                    Region: {item.region?.name || item.region}
                  </p>
                )}
              </div>
              <div className="text-right">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  Location
                </span>
              </div>
            </div>
          </div>
        );

      case "job-seekers":
        return (
          <div className="p-4 border border-gray-200 rounded-lg bg-white hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {item.first_name || ""} {item.last_name || ""}
                </h3>
                <p className="text-sm text-gray-600">
                  Email: {item.email || "N/A"}
                </p>
                <p className="text-sm text-gray-600">
                  Phone: {item.phone || "N/A"}
                </p>
                {(item.city || item.state) && (
                  <p className="text-sm text-gray-600">
                    Location: {item.city || ""}
                    {item.city && item.state ? " / " : ""}
                    {item.state || ""}
                  </p>
                )}
              </div>
              <div className="text-right">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  Job Seeker
                </span>
              </div>
            </div>
          </div>
        );

      case "business-users":
        return (
          <div className="p-4 border border-gray-200 rounded-lg bg-white hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {item.first_name || ""} {item.last_name || ""}
                </h3>
                <p className="text-sm text-gray-600">
                  Email: {item.email || "N/A"}
                </p>
                <p className="text-sm text-gray-600">
                  Role:{" "}
                  {item.roles && item.roles.length > 0
                    ? item.roles[0].name.replace("_", " ")
                    : item.role || item.user_type || "N/A"}
                </p>
                <p className="text-sm text-gray-600">
                  Company: {item.company?.name || item.company_name || "N/A"}
                </p>
                {item.phone && (
                  <p className="text-sm text-gray-600">
                    Phone: {item.phone}
                  </p>
                )}
              </div>
              <div className="text-right">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-800">
                  Business User
                </span>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="p-4 border border-gray-200 rounded-lg bg-white">
            <pre className="text-sm text-gray-600">
              {JSON.stringify(item, null, 2)}
            </pre>
          </div>
        );
    }
  };

  // Render pagination
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 rounded-b-lg">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing{" "}
              <span className="font-medium">{(currentPage - 1) * 10 + 1}</span>{" "}
              to{" "}
              <span className="font-medium">
                {Math.min(currentPage * 10, totalResults)}
              </span>{" "}
              of <span className="font-medium">{totalResults}</span> results
            </p>
          </div>
          <div>
            <nav
              className="isolate inline-flex -space-x-px rounded-md shadow-sm"
              aria-label="Pagination"
            >
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">Previous</span>
                <i className="fas fa-chevron-left h-5 w-5" aria-hidden="true" />
              </button>
              {pages.map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                    page === currentPage
                      ? "z-10 bg-primary-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                      : "text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">Next</span>
                <i
                  className="fas fa-chevron-right h-5 w-5"
                  aria-hidden="true"
                />
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="pb-12">
        <div className="bg-white shadow rounded-lg">
          {/* Header */}
          <div className="px-6 py-6 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Global Search
            </h1>

            {/* Search Bar */}
            <div className="w-full max-w-2xl">
              <SearchBar
                searchValue={searchQuery}
                placeholder="Search across all entities..."
                searchFunction={handleSearchChange}
              />
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors duration-200`}
                >
                  <i className={tab.icon} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <span className="ml-3 text-gray-600">
                  Searching...
                </span>
              </div>
            ) : searchQuery && searchResults.length === 0 ? (
              <div className="text-center py-12">
                <i className="far fa-search text-4xl text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No results found
                </h3>
                <p className="text-gray-600">
                  Try adjusting your search terms or switching to a different
                  tab.
                </p>
              </div>
            ) : searchResults.length > 0 ? (
              <div className="space-y-4">
                {searchResults.map((item, index) => (
                  <div key={index}>{renderResultItem(item, activeTab)}</div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <i className="far fa-search text-4xl text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Start searching
                </h3>
                <p className="text-gray-600">
                  Enter a search term above to find companies, brands, regions,
                  locations, job seekers, and business users.
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {searchResults.length > 0 && renderPagination()}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default GlobalSearch;
